/**
 * ReviewListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  ModalLayout,
  ShopInformationList,
} from 'components';
import {
  Avatar,
  Button,
  DatePicker,
  Input,
  Radio,
  Rate,
  Select,
  Table,
  Spin,
  Switch,
  Tag,
  Flex,
  InputNumber,
} from 'antd';
import moment from 'moment';
import { strCut, stringToMoneyFormat } from 'utils';
import { LoadingOutlined } from '@ant-design/icons';
import ReviewDetail from '../ReviewDetail';
import dayjs from 'dayjs';
import { downloadXlsx } from 'utils/XlsxDownloader';

const INITIAL_VALUE = {
  type: '',
  best: '',
  reserve: '',
  start: '',
  end: '',
  keyword: 'review_content',
  search: '',
  minScore: null,
  maxScore: null,
  isAnswer: null,
};

/**----------------------------------------
 * [Component] 총관리자: 리뷰목록 - 컨테이너
 * ----------------------------------------
 */
const ReviewListPresenter = ({
  permission,
  loading,
  reviewList,
  currentShop,
  setCurrentShop,
  // 리뷰 상세보기 모달
  reviewDetailModal,
  setReviewDetailModal,
  onChangeReviewDetail,
  onUpdateReview,
  onDeleteReview,
  setLoading,
  onReserveReview,
  onSelect,
  selectedList,
  onBulkDelete,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [filterItem, setFilterItem] = React.useState(INITIAL_VALUE);
  const [filterList, setFilterList] = React.useState([...reviewList]);

  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(0);
  const [memo, setMemo] = React.useState('');

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '리뷰 유형',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.type}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, type: e.target.value }))
              }
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'TEXT'}>텍스트</Radio>
              <Radio value={'PHOTO'}>포토/영상</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '베스트 선정여부',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.best}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, best: e.target.value }))
              }
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'Y'}>베스트 리뷰</Radio>
              <Radio value={'N'}>일반</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '적립금 적립여부',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.reserve}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, reserve: e.target.value }))
              }
            >
              <Radio value={''}>전체</Radio>
              <Radio value={'Y'}>적립됨</Radio>
              <Radio value={'N'}>미적립</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '검색어',
      direction: 'column',
      form: [
        {
          input: (
            <Flex>
              <Select
                size="large"
                value={filterItem.keyword}
                options={[
                  { label: '리뷰 내용', value: 'review_content' },
                  { label: '상품 번호', value: 'product_code' },
                  { label: '상품 명', value: 'product_name' },
                  { label: '회원 명', value: 'user_name' },
                ]}
                style={{ ...styles.form.input.large }}
                onChange={(e) =>
                  setFilterItem((prev) => ({ ...prev, keyword: e }))
                }
              />
              <Input
                size="large"
                placeholder="검색어를 입력해주세요"
                style={{ ...styles.form.input.large, width: 350 }}
                value={filterItem.search}
                onChange={(e) =>
                  setFilterItem((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </Flex>
          ),
        },
      ],
    },
    {
      label: '리뷰 작성일',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                onChange={(e) => {
                  setFilterItem((prev) => ({
                    ...prev,
                    start: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end: dayjs().endOf('day'),
                  }));
                }}
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>

              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={filterItem.start ? dayjs(filterItem.start) : ''}
                  onChange={(e) =>
                    setFilterItem((prev) => ({ ...prev, start: e }))
                  }
                  maxDate={filterItem.end}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={filterItem.end ? dayjs(filterItem.end) : ''}
                  onChange={(e) =>
                    setFilterItem((prev) => ({ ...prev, end: e }))
                  }
                  minDate={filterItem.start}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
    {
      label: '별점',
      form: [
        {
          input: (
            <Flex align="center" gap={4}>
              <InputNumber
                size="large"
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                min={1}
                max={filterItem.maxScore}
                value={filterItem.minScore}
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    minScore: e,
                  }))
                }
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <InputNumber
                size="large"
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large }}
                value={filterItem.maxScore}
                onChange={(e) =>
                  setFilterItem((prev) => ({
                    ...prev,
                    maxScore: e,
                  }))
                }
                min={filterItem.minScore}
                max={5}
              />
            </Flex>
          ),
        },
      ],
    },
    {
      label: '답글 여부',
      form: [
        {
          input: (
            <Radio.Group
              value={filterItem.isAnswer}
              onChange={(e) =>
                setFilterItem((prev) => ({ ...prev, isAnswer: e.target.value }))
              }
            >
              <Radio value={null}>전체</Radio>
              <Radio value={'Y'}>작성</Radio>
              <Radio value={'N'}>미작성</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
  ];

  const col = [
    {
      title: '이름(계정)',
      dataIndex: 'user',
      key: 'user',
      width: 180,
      render: (_item) => (
        <>
          <Avatar style={{ marginRight: 5 }}>{_item.user_name[0]}</Avatar>
          {strCut(_item.user_name, 10)}({strCut(_item.user_account, 10)})
        </>
      ),
    },
    {
      title: '상품명',
      dataIndex: 'product',
      key: 'product',
      width: 350,
      render: (_item) => (
        <>
          <a>
            <Avatar shape="square" size={25} style={{ marginRight: 5 }} />
            {strCut(_item.product_name, 20)}
          </a>
        </>
      ),
    },
    {
      title: '리뷰내용',
      dataIndex: 'review_content',
      key: 'review_content',
      width: 350,
      render: (item, row) => (
        <a onClick={() => setReviewDetailModal(row)}>
          {row.review_answer && <Tag color="magenta">답변</Tag>}
          {strCut(item, 25)}
        </a>
      ),
    },
    {
      title: '리뷰점수',
      dataIndex: 'review_score',
      key: 'review_score',
      width: 170,
      sorter: {
        compare: (a, b) => a.review_score - b.review_score,
        multiple: 1,
      },
      render: (_item) => (
        <>
          <Rate className="rate" size="small" value={_item} disabled />
          <span style={{ position: 'absolute' }}>({_item}/5)</span>
        </>
      ),
    },

    {
      title: '등록일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 155,
      sorter: {
        compare: (a, b) => a.created_at - b.created_at,
        multiple: 2,
      },
      render: (item) => moment(item * 1000).format('YYYY-MM-DD(HH:mm)'),
    },
    {
      title: '베스트 리뷰',
      dataIndex: 'best_review',
      key: 'is_show',
      width: 100,
      render: (item) => (item === 'Y' ? 'O' : 'X'),
    },
    {
      title: '적립금',
      dataIndex: 'point_history',
      key: 'point_history',
      width: 100,
      render: (item) =>
        item ? stringToMoneyFormat(item.point) : <Tag>미적립</Tag>,
      sorter: (a, b) =>
        (a.point_history?.point ?? 0) - (b.point_history?.point ?? 0),
    },
    {
      title: '노출여부',
      dataIndex: 'is_show',
      key: 'is_show',
      width: 125,
      fixed: 'right',
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation(); // 클릭 이벤트 무시 (Name 셀은 클릭 허용하지 않음)
        },
      }),
      render: (item, row) => (
        <>
          <Switch
            checked={item === 'Y' ? true : false}
            checkedChildren="노출"
            unCheckedChildren="숨김"
            onChange={(e) => onUpdateReview(row.review_id, e ? 'Y' : 'N')}
          />
          &nbsp;
          {item === null && <span>(심사전)</span>}
        </>
      ),
      filters: [
        { text: '심사전', value: null },
        { text: '노출 (심사완료)', value: 'Y' },
        { text: '숨김 (심사완료)', value: 'N' },
      ],
      onFilter: (value, { is_show }) => is_show === value,
    },
  ];
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  const sortedReviewList = reviewList?.sort(
    (a, b) => a.review_score - b.review_score
  );
  const productCount = reviewList.reduce(
    (acc, cur) =>
      acc.indexOf(cur.product_id) >= 0
        ? acc
        : [...(acc ? acc : []), cur.product_id],
    [] // 초기값
  );

  const reviewCount = reviewList.length;
  const lowScore = sortedReviewList[0]?.review_score;
  const highScore = sortedReviewList[reviewCount - 1]?.review_score;
  const avgScore = (
    sortedReviewList.reduce((acc, cur) => (acc += cur.review_score), 0) /
    reviewCount
  ).toFixed(2);

  /* ===== FUNCTION ===== */
  /**
   * 리뷰 조건 검색
   * --
   * @param {string} t
   */
  const handleSearch = (t) => {
    setLoading(true);
    const searchType = t.toLocaleUpperCase();

    let arr = [...reviewList];
    switch (searchType) {
      case 'RESET':
        setFilterItem(INITIAL_VALUE);
        break;
      case 'SEARCH':
        const {
          start,
          end,
          keyword,
          search,
          type,
          best,
          minScore,
          maxScore,
          reserve,
          isAnswer,
        } = filterItem;

        if (search) {
          arr = arr.filter((review) => {
            switch (keyword) {
              case 'user_name':
                return review.user[keyword].includes(search);
              case 'product_code':
              case 'product_name':
                return review.product[keyword].includes(search);
              default:
                return review[keyword].includes(search);
            }
          });
        }

        if (type) {
          arr = arr.filter((review) => {
            if (type === 'TEXT') {
              return !review.review_file.length;
            } else {
              return review.review_file.length;
            }
          });
        }

        if (best) {
          arr = arr.filter((review) => review.best_review === best);
        }

        if (reserve) {
          arr = arr.filter((review) => {
            if (reserve === 'Y') {
              return review.point_history;
            } else {
              return !review.point_history;
            }
          });
        }

        if (start || end) {
          const filterType = start && end ? 'BETWEEN' : start ? 'START' : 'END';

          switch (filterType) {
            case 'START':
              arr = arr.filter(
                (review) => review.created_at >= dayjs(start).startOf().unix()
              );
              break;
            case 'END':
              arr = arr.filter(
                (review) => review.created_at <= dayjs(end).endOf('day').unix()
              );
              break;
            case 'BETWEEN':
              arr = arr.filter(
                (review) =>
                  review.created_at >= dayjs(start).startOf().unix() &&
                  review.created_at <= dayjs(end).endOf('day').unix()
              );
              break;
            default:
              break;
          }
        }

        if (minScore || maxScore) {
          arr = arr.filter(
            (review) =>
              review.review_score >= (minScore ?? 0) &&
              review.review_score <= (maxScore ?? 0)
          );
        }

        if (isAnswer) {
          arr = arr.filter((review) => {
            if (isAnswer === 'Y') {
              return review.review_answer;
            } else {
              return !review.review_answer;
            }
          });
        }

        break;
      default:
        break;
    }

    const sort = arr.sort((a, b) => b.created_at - a.created_at);

    setTimeout(() => {
      setLoading(false);
      setFilterList(sort);
    }, 1000);
  };

  /* ===== Hooks ===== */
  React.useEffect(() => {
    setFilterList(reviewList);
  }, [reviewList]);

  return (
    <>
      {/* === Loading - start === */}
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        {/* ===== 헤더&필터 - start ===== */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 타이틀 === */}
          <Title size={4} style={{ marginBottom: 10 }}>
            리뷰목록
          </Title>

          {/* === 필터 - start === */}
          <ExpandableContainer borderRadius={4} padding={20}>
            {formItem.map((_item, _index) => (
              <div
                key={`form_${_index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 0px',
                }}
              >
                <div style={{ width: 120, fontSize: 13, fontWeight: 400 }}>
                  <label>{_item.label}</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {_item.form.map((_subitem, _subkey) => (
                    <React.Fragment key={`input_${_subkey}`}>
                      {_subitem.input}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </ExpandableContainer>

          {/* 검색/초기화 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              marginTop: 13,
            }}
          >
            <Button
              size="large"
              type="primary"
              style={{
                ...styles.button,
                background: 'var(--primary-color)',
              }}
              onClick={() => handleSearch('search')}
            >
              검색
            </Button>
            <Button
              size="large"
              style={{ ...styles.button }}
              onClick={() => handleSearch('reset')}
            >
              초기화
            </Button>
          </div>
          {/* 검색/초기화 버튼 끝 */}
          {/* === 필터 - end === */}
        </Content>
        {/* ===== 헤더&필터 - end ===== */}

        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          {/* === 통계 - start === */}
          <Content
            maxWidth={'100%'}
            padding={0}
            backgroundColor={'none'}
            style={{ marginBottom: 4, display: 'flex' }}
          >
            {[
              {
                title: '상품 수',
                value: stringToMoneyFormat(productCount.length),
                suffix: '개',
              },
              {
                title: '리뷰 수',
                value: stringToMoneyFormat(reviewCount),
                suffix: '개',
              },
              {
                title: '최저 평점',
                value: lowScore,
                suffix: '점',
              },
              {
                title: '평균 평점',
                value: avgScore,
                suffix: '점',
              },
            ].map((_item, _index) => (
              <div
                key={`${_item.label}-${_index}`}
                className="boxShadow"
                style={{
                  background: '#FFFFFF',
                  borderRadius: 4,
                  width: `calc(100% / 4)`,
                  margin: '0 2px',
                  padding: '26px 14px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Statistic
                  title={_item.title}
                  value={_item.value}
                  suffix={_item.suffix}
                  direction={'row'}
                  style={{ justifyContent: 'center' }}
                />
              </div>
            ))}
          </Content>
          {/* === 통계 - end === */}

          {/* ===== 하단컨텐츠 - start ===== */}
          <Row gutter={[1, 1]} className="boxShadow">
            {/* === 업체목록 - start === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <ShopInformationList
                  onChange={setCurrentShop}
                  maxHeight={611}
                />
              </Col>
            )}
            {/* === 업체목록 - end === */}

            {/* === 리뷰목록 - start === */}
            <Col x={permission === 'ADMIN' ? 20 : 24}>
              <Content
                maxWidth={'100%'}
                width={1400}
                height={'100%'}
                minHeight={490}
                maxHeight={662}
                borderRadius={4}
                padding={20}
              >
                {/* === 헤더 === */}
                <div>
                  <Title
                    size={4}
                    style={{ marginBottom: 10 }}
                    subButton={
                      <Flex gap={4}>
                        <Button
                          onClick={() => {
                            downloadXlsx({
                              columns: col,
                              rows: filterList?.map((_item) => ({
                                ..._item,
                                product: _item?.product?.product_name,
                                user: `${_item?.user?.user_name}(${_item?.user?.user_account})`,
                                point_history: _item?.point_history?.point,
                              })),
                              fileName: '리뷰',
                            });
                          }}
                        >
                          엑셀로 다운로드
                        </Button>

                        {permission === 'ADMIN' ? (
                          <>
                            <Button
                              disabled={!selectedList.length}
                              onClick={() => setIsOpen(!isOpen)}
                            >
                              {selectedList.length}명 적립금 지급
                            </Button>

                            <Button
                              danger
                              disabled={!selectedList.length}
                              onClick={() => onBulkDelete()}
                            >
                              {selectedList.length} 리뷰 삭제
                            </Button>
                          </>
                        ) : null}
                      </Flex>
                    }
                    // subButton={<Button type="primary">일괄노출처리</Button>}
                  >
                    <span>
                      검색결과 총 <a>{filterList.length}</a>건
                    </span>
                  </Title>
                </div>

                {/* === 리뷰목록 === */}
                <Table
                  rowSelection={{
                    type: 'checkbox',
                    onSelect: (record, selected) =>
                      onSelect(selected ? 'checked' : 'unchecked', record),
                    onSelectAll: (selected, selectedRows) =>
                      onSelect(
                        selected ? 'all-checked' : 'all-unchecked',
                        selectedRows
                      ),
                    selectedRowKeys: selectedList?.map(
                      (review) => review?.review_id
                    ),
                    // getCheckboxProps: (record) => ({
                    //   disabled: record?.point_history,
                    // }),
                  }}
                  size="small"
                  scroll={{
                    x: 1250,
                    y: 49 * 10,
                  }}
                  onRow={(record, index) => {
                    // console.log('index: ', index);

                    return {
                      onClick: () => setReviewDetailModal(record), // 행 클릭 시 이벤트 처리
                    };
                  }}
                  dataSource={filterList}
                  columns={col}
                />
              </Content>
            </Col>
            {/* === 리뷰목록 - end === */}
          </Row>
          {/* ===== 하단컨텐츠 - end ===== */}
        </Content>
      </Spin>
      {/* === Loading - start === */}

      {/* === 리뷰 상세보기: start === */}
      <ModalLayout
        type={'drawer'}
        open={reviewDetailModal !== null}
        onCancel={() => setReviewDetailModal(null)}
        centered
        closable
        title={`리뷰 상세정보`}
        width={690}
        footer={
          <Button onClick={() => setReviewDetailModal(null)}>닫기</Button>
        }
        // style={{ padding: 0 }}
        styles={{
          body: { padding: 5 },
        }}
      >
        {reviewDetailModal && (
          <ReviewDetail
            defaultReviewId={reviewDetailModal.review_id}
            rawData={reviewDetailModal}
            onChange={onChangeReviewDetail}
            onDelete={onDeleteReview}
            onReserveReview={onReserveReview}
            permission={permission}
          />
        )}
      </ModalLayout>
      {/* === 리뷰 상세보기: end === */}

      <ModalLayout
        open={isOpen}
        onCancel={() => setIsOpen(!isOpen)}
        width={300}
        centered
      >
        <Content maxWidth={'100%'} padding={0}>
          <Flex gap={4} vertical>
            <Flex gap={4}>
              <InputNumber
                value={inputValue}
                onChange={(e) => setInputValue(e)}
                placeholder="지급 적립금"
                style={{ flex: 1 }}
                suffix={'P'}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
              <Button
                type="primary"
                onClick={() =>
                  onReserveReview(
                    selectedList,
                    { reserve: inputValue, memo: memo },
                    () => {
                      setIsOpen(false);
                      setInputValue(0);
                    }
                  )
                }
              >
                지급
              </Button>
            </Flex>

            <Flex flex={1}>
              <Input.TextArea
                placeholder="적립사유"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Flex>
          </Flex>
        </Content>
      </ModalLayout>
    </>
  );
};

export default ReviewListPresenter;
